/**
 * Key Result — show view on the shared Inspector grammar (one-grammar law).
 * Overview leads with the meaningful fields (progress / values / status); the
 * domain-specific progress widget (`ProgressBar` + `keyResultProgress`) is
 * composed INSIDE a full-width Overview field. Relations link the parent
 * Objective (and the optional Metric); ids/timestamps sink to a muted Technical
 * tail; Audit closes the page.
 */
import { useShow, useGo, type HttpError } from "@refinedev/core";
import { Target } from "@/components/ui/icon";

import type { KeyResult } from "@aos/shared";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { formatDateTime, humanize } from "@/lib/format";
import { keyResultProgress, ProgressBar } from "@/resources/key-results/progress";

/** KeyResultShow — key result detail with progress, parent objective + metric. */
export function KeyResultShow() {
  const go = useGo();
  const { query } = useShow<KeyResult, HttpError>({ resource: "key-results" });
  const { data, isLoading, isError, error, refetch } = query;
  const kr = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !kr) {
    return (
      <ShowPageError
        message={error?.message ?? "Key result not found."}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const pct = keyResultProgress(kr);
  const unit = kr.unit ? ` ${kr.unit}` : "";

  return (
    <Inspector>
      <InspectorHeader
        title={kr.title}
        subtitle="A key result — its target, current value, and progress toward the objective."
        status={kr.status}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              go({ to: { resource: "key-results", action: "edit", id: kr.id } })
            }
          >
            <Icon name="edit" size={16} className="mr-2" /> Edit
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Status">{humanize(kr.status)}</Field>
          <Field label="Current">
            {kr.current_value ?? 0}
            {unit}
          </Field>
          <Field label="Target">
            {kr.target_value != null ? `${kr.target_value}${unit}` : null}
          </Field>
          <Field label="Unit">{kr.unit}</Field>
          <Field label="Progress" full>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {kr.current_value ?? 0}
                  {unit} of {kr.target_value ?? "—"}
                  {kr.target_value != null ? unit : ""}
                </span>
                <span className="font-medium">{pct}%</span>
              </div>
              <ProgressBar value={pct} />
            </div>
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "objective",
                  label: "Objective",
                  icon: Target,
                  resource: "objectives",
                  id: kr.objective_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={kr.id} />
          </Field>
          <Field label="Objective">
            <RefLink resource="objectives" id={kr.objective_id} />
          </Field>
          <Field label="Created">{formatDateTime(kr.created_at)}</Field>
          <Field label="Updated">{formatDateTime(kr.updated_at)}</Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="key-results" entityId={kr.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default KeyResultShow;
